import React, { useState, useEffect } from "react";
import {
  ManageAccounts as PersonIcon,
  Settings as SettingsIcon,
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Article as ArticleIcon,
  Collections as BannerIcon,
  MenuBook as BookIcon,
  Category as CategoryIcon,
  Language as LanguageIcon,
  Business as SectorIcon,
  Public as CountryIcon,
  TempleHindu as TempleHinduIcon,
  Pages,
  Gavel as GavelIcon,
  Info,
  Policy,
  MiscellaneousServices,
  FormatListBulleted as ListsIcon,
  PlaylistAdd as FormsIcon,
  AutoStories as AddBookIcon,
  AddLocationAlt as AddTirthIcon,
  PostAdd as AddBlogIcon,
  AddPhotoAlternate as AddBannerIcon,
  AccountBox as AuthorIcon,
} from "@mui/icons-material";
import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./Sidebar.module.scss";
import { handleLogout } from "../../pages/Auth/common";
import { useAuth } from "../../routes/middleware";

const Sidebar = () => {
  const theme = useTheme();
  const location = useLocation();
  const { setToken } = useAuth();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [isOpen, setIsOpen] = useState(isDesktop);
  const [openSections, setOpenSections] = useState({
    lists: true,
    forms: false,
    pages: false,
    miscellaneous: false,
  });
  const navigate = useNavigate();

  // When screen size changes, update sidebar open state
  useEffect(() => {
    setIsOpen(isDesktop);
  }, [isDesktop]);

  // Check if current path matches the menu item path
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if any child route is active
  const isGroupActive = (paths) => {
    return paths.some((path) => location.pathname.startsWith(path));
  };

  // Auto-expand sections based on path
  useEffect(() => {
    const path = location.pathname;
    setOpenSections((prev) => {
      const updated = { ...prev };
      if (
        path.startsWith("/book/list") ||
        path.startsWith("/article/list") ||
        path.startsWith("/tirth/list") ||
        path.startsWith("/blog/list") ||
        path.startsWith("/banner/list")
      ) {
        updated.lists = true;
      }
      if (
        path.startsWith("/book/add") ||
        path.startsWith("/article/add") ||
        path.startsWith("/tirth/add") ||
        path.startsWith("/blog/add") ||
        path.startsWith("/banner/add") ||
        path.includes("/edit/")
      ) {
        updated.forms = true;
      }
      if (path.startsWith("/pages/") || path.includes("/about-us/")) {
        updated.pages = true;
      }
      if (
        path.startsWith("/miscellaneous") ||
        path.startsWith("/users") ||
        path.startsWith("/authors")
      ) {
        updated.miscellaneous = true;
      }
      return updated;
    });
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsOpen((prev) => !prev);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (!isDesktop) {
      setIsOpen(false);
    }
  };

  const handleSectionClick = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <>
      {!isOpen && (
        <Box position="fixed" zIndex="100" top="10" height="40px" width="40px">
          <Button
            className={styles["toggle-btn"]}
            variant="contained"
            onClick={toggleSidebar}
          >
            <MenuIcon />
          </Button>
        </Box>
      )}
      <div
        className={`${styles.sidebar} ${isOpen ? styles.open : styles.closed}`}
      >
        {!isDesktop && (
          <Box display="flex" justifyContent="flex-end" p={2} pr={3}>
            <IconButton onClick={toggleSidebar}>
              <CloseIcon fontSize="large" style={{ color: "#fff" }} />
            </IconButton>
          </Box>
        )}

        <List className={styles["sidebar-nav"]} color="primary">
          <ListItem
            onClick={() => handleSectionClick("lists")}
            className={`${styles["menu-item"]} ${
              isGroupActive(["/book/list", "/tirth/list", "/blog/list", "/banner/list"])
                ? styles.active
                : ""
            }`}
          >
            <ListItemIcon>
              <ListsIcon className={styles["menu-icon"]} />
            </ListItemIcon>
            <ListItemText primary="Lists" />
            {openSections.lists ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItem>

          <Collapse in={openSections.lists} timeout="auto" unmountOnExit>
            <List component="div" className={styles["nested-list"]}>
              <ListItem
                onClick={() => handleMenuItemClick("/book/list")}
                className={`${styles["nested-item"]} ${
                  isActive("/book/list") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <BookIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Granth List" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/article/list")}
                className={`${styles["nested-item"]} ${
                  isActive("/article/list") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <ArticleIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Article List" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/tirth/list")}
                className={`${styles["nested-item"]} ${
                  isActive("/tirth/list") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <TempleHinduIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Tirth List" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/blog/list")}
                className={`${styles["nested-item"]} ${
                  isActive("/blog/list") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <ArticleIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Blog List" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/banner/list")}
                className={`${styles["nested-item"]} ${
                  isActive("/banner/list") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <BannerIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Banner Carousel" />
              </ListItem>
            </List>
          </Collapse>

          <ListItem
            onClick={() => handleSectionClick("forms")}
            className={`${styles["menu-item"]} ${
              isGroupActive([
                "/book/add",
                "/tirth/add",
                "/blog/add",
                "/banner/add",
                "/book/edit",
                "/tirth/edit",
                "/blog/edit",
                "/banner/edit",
              ])
                ? styles.active
                : ""
            }`}
          >
            <ListItemIcon>
              <FormsIcon className={styles["menu-icon"]} />
            </ListItemIcon>
            <ListItemText primary="Forms" />
            {openSections.forms ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItem>

          <Collapse in={openSections.forms} timeout="auto" unmountOnExit>
            <List component="div" className={styles["nested-list"]}>
              <ListItem
                onClick={() => handleMenuItemClick("/book/add")}
                className={`${styles["nested-item"]} ${
                  isActive("/book/add") || location.pathname.startsWith("/book/edit") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <AddBookIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Add Granth" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/article/add?type=literature")}
                className={`${styles["nested-item"]} ${
                  isActive("/article/add") || location.pathname.startsWith("/article/edit") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <ArticleIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Add Article" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/tirth/add")}
                className={`${styles["nested-item"]} ${
                  isActive("/tirth/add") || location.pathname.startsWith("/tirth/edit") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <AddTirthIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Add Tirth" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/blog/add")}
                className={`${styles["nested-item"]} ${
                  isActive("/blog/add") || location.pathname.startsWith("/blog/edit") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <AddBlogIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Add Blog" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/banner/add")}
                className={`${styles["nested-item"]} ${
                  isActive("/banner/add") || location.pathname.startsWith("/banner/edit") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <AddBannerIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Add Banner" />
              </ListItem>
            </List>
          </Collapse>

          <ListItem
            onClick={() => handleSectionClick("pages")}
            className={`${styles["menu-item"]} ${
              isGroupActive(["/pages", "/about-us/add", "/about-us/edit"]) ? styles.active : ""
            }`}
          >
            <ListItemIcon>
              <Pages className={styles["menu-icon"]} />
            </ListItemIcon>
            <ListItemText primary="Pages" />
            {openSections.pages ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItem>

          <Collapse in={openSections.pages} timeout="auto" unmountOnExit>
            <List component="div" className={styles["nested-list"]}>
              <ListItem
                onClick={() => handleMenuItemClick("/pages/about-us")}
                className={`${styles["nested-item"]} ${
                  isActive("/pages/about-us") || location.pathname.startsWith("/about-us/") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <Info className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="About Us" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/pages/privacy-policy")}
                className={`${styles["nested-item"]} ${
                  isActive("/pages/privacy-policy") || location.pathname.startsWith("/pages/policy/") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <Policy className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Privacy Policy" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/pages/terms-condition")}
                className={`${styles["nested-item"]} ${
                  isActive("/pages/terms-condition") || location.pathname.startsWith("/pages/terms-condition/") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <GavelIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Terms & Conditions" />
              </ListItem>
            </List>
          </Collapse>

          <ListItem
            onClick={() => handleSectionClick("miscellaneous")}
            className={`${styles["menu-item"]} ${
              isGroupActive(["/miscellaneous", "/users", "/authors"]) ? styles.active : ""
            }`}
          >
            <ListItemIcon>
              <MiscellaneousServices className={styles["menu-icon"]} />
            </ListItemIcon>
            <ListItemText primary="Miscellaneous" />
            {openSections.miscellaneous ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )}
          </ListItem>

          <Collapse
            in={openSections.miscellaneous}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" className={styles["nested-list"]}>
              <ListItem
                onClick={() => handleMenuItemClick("/miscellaneous/categories")}
                className={`${styles["nested-item"]} ${
                  isActive("/miscellaneous/categories") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <CategoryIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Categories" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/miscellaneous/languages")}
                className={`${styles["nested-item"]} ${
                  isActive("/miscellaneous/languages") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <LanguageIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Languages" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/miscellaneous/sectors")}
                className={`${styles["nested-item"]} ${
                  isActive("/miscellaneous/sectors") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <SectorIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Sectors" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/miscellaneous/locations")}
                className={`${styles["nested-item"]} ${
                  isActive("/miscellaneous/locations") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <CountryIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Locations" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/users")}
                className={`${styles["nested-item"]} ${
                  isActive("/users") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <PersonIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Manage Access" />
              </ListItem>

              <ListItem
                onClick={() => handleMenuItemClick("/authors")}
                className={`${styles["nested-item"]} ${
                  isActive("/authors") ? styles.active : ""
                }`}
              >
                <ListItemIcon>
                  <AuthorIcon className={styles["menu-icon"]} />
                </ListItemIcon>
                <ListItemText primary="Authors" />
              </ListItem>
            </List>
          </Collapse>

          <ListItem
            onClick={() => handleMenuItemClick("/settings")}
            className={`${styles["menu-item"]} ${
              isActive("/settings") ? styles.active : ""
            }`}
          >
            <ListItemIcon>
              <SettingsIcon className={styles["menu-icon"]} />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItem>

          <ListItem
            onClick={() => handleLogout(navigate, setToken)}
            className={styles["menu-item"]}
          >
            <ListItemIcon>
              <ExitToAppIcon className={styles["menu-icon"]} />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
      </div>
    </>
  );
};

export default Sidebar;
